import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  AdminEnvelope,
  AdminPermission,
  AdminUserPermission,
} from '@/client/types/admin/users-orgs';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import useGetMutationFn from '../use-get-mutation-fn';
import useGetQueryFn from '../use-get-query-fn';
import { toFormData } from './lib/form';
import { useClientStore } from '@/client/stores/client';
import { useUserStore } from '@/client/stores/user';
import { withErrorHandling } from '@/client/utils/errors';

export const adminPermissionKeys = {
  all: ['admin', 'permissions'] as const,
  registry: (params: Record<string, unknown>) =>
    [...adminPermissionKeys.all, 'registry', params] as const,
  forUser: (uid: number | null) =>
    [...adminPermissionKeys.all, 'user', uid] as const,
};

export const usePermissionRegistryQuery = (
  params: {
    page?: number;
    per_page?: number;
    code?: string;
    category?: string;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  } = {}
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminPermission[]>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching permissions',
    endpoint: '/api/admin/permissions',
  });

  return useQuery({
    queryKey: adminPermissionKeys.registry(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    // Keep the prior page visible while the next loads so the pager doesn't
    // flash an empty table between pages.
    placeholderData: keepPreviousData,
    select: (response) => response?.data ?? [],
  });
};

export const useUserPermissionsQuery = (uid: number | null, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminUserPermission[]>>({
    defaultErrorMessage:
      'An unexpected error occurred while fetching user permissions',
    endpoint: `/api/admin/user_permissions/${uid}`,
  });

  return useQuery({
    queryKey: adminPermissionKeys.forUser(uid),
    queryFn: () => queryFn({}),
    enabled: Boolean(uid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data ?? [],
  });
};


export const useCreatePermissionMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<FormData, AdminEnvelope<AdminPermission>>({
    defaultErrorMessage: 'An unexpected error occurred while creating the permission',
    endpoint: '/api/admin/permissions',
    method: 'POST',
  });

  return useMutation({
    mutationFn: (payload: {
      code: string;
      name: string;
      description: string;
      category: string;
    }) => mutationFn(toFormData(payload as unknown as Record<string, unknown>)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPermissionKeys.all });
    },
  });
};

export const useUpdatePermissionMutation = () => {
  const queryClient = useQueryClient();
  const request = useClientStore((state) => state.request);

  return useMutation({
    mutationFn: withErrorHandling(
      ({
        permid,
        ...rest
      }: Partial<Omit<AdminPermission, 'permid'>> & { permid: number }) =>
        request<AdminEnvelope<AdminPermission>>(
          `/api/admin/permissions/${permid}`,
          'PUT',
          toFormData(rest as unknown as Record<string, unknown>)
        ),
      'An unexpected error occurred while updating the permission'
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPermissionKeys.all });
    },
  });
};

/** `DELETE /api/admin/permissions/<permid>`. */
export const useDeletePermissionMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { permid: number },
    AdminEnvelope<{ message: string }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while deleting the permission',
    endpoint: (payload) => `/api/admin/permissions/${payload.permid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPermissionKeys.all });
    },
  });
};

/** `POST /api/admin/user_permissions` — JSON. Assign permissions to a user. */
export const useAssignUserPermissionMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { uid: number; permids: number[] },
    AdminEnvelope<{ success: boolean }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while assigning permissions',
    endpoint: '/api/admin/user_permissions',
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminPermissionKeys.forUser(variables.uid),
      });
      void useUserStore.getState().refreshCachedUser();
    },
  });
};

/**
 * Revoke / restore a permission assignment.
 *
 * Both are **POST**, not PUT — inconsistent with every other state-change route
 * in the admin surface, but that is what ships (gap C-5). There is no DELETE.
 */
export const useToggleUserPermissionMutation = () => {
  const queryClient = useQueryClient();

  const deactivate = useGetMutationFn<
    { upermid: number },
    AdminEnvelope<{ message: string }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while revoking the permission',
    endpoint: (payload) =>
      `/api/admin/user_permissions/${payload.upermid}/deactivate`,
    method: 'POST',
  });

  const activate = useGetMutationFn<
    { upermid: number },
    AdminEnvelope<{ message: string }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while restoring the permission',
    endpoint: (payload) =>
      `/api/admin/user_permissions/${payload.upermid}/activate`,
    method: 'POST',
  });

  return useMutation({
    mutationFn: ({ upermid, active }: { upermid: number; active: boolean }) =>
      active ? activate({ upermid }) : deactivate({ upermid }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPermissionKeys.all });
      void useUserStore.getState().refreshCachedUser();
    },
  });
};
